import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Button from '../../common/components/button';
import Div from '../../common/components/div';
import H2 from '../../common/components/h2';
import H3 from '../../common/components/h3';
import StackingButtons from '../../common/components/StackingButtons';
import TranslatedSpan from '../../common/components/TranslatedSpan';
import ValidationAlertMessage from '../../common/components/ValidationAlertMessage';
import capitalizeFirstLetter from '../../common/functions/capitalizeFirstLetter';
import capitalizeWords from '../../common/functions/capitalizeWords';
import translate from '../../common/functions/translate';
import { useConversion } from '../../common/hooks/use.analytics';
import useTrait from '../../common/hooks/useTrait';
import is from '../../common/objects/is';
import ErrorAlert from '../../common/components/ErrorAlert';
import {
    ApiError,
    get400InvalidUsernameOrPasswordError,
    getUnlockAccountError,
    InvalidUsernameOrPasswordError,
    isAlreadyUnlockedMessage
} from '../../common/functions/getApiErrorMessage';
import { SecretQuestionDropdown } from '../../common/components/SecretQuestionDropdown';
import { NewPassword } from '../../common/components/NewPassword/NewPassword';
import { AllFieldsRequired } from '../../common/components/AllFieldsRequired';
import useUnlockAccountApi from '../../common/hooks/useUnlockAccountApi';
import { globalData } from '../../globalContext';
import { createOrUnlockAccountData } from '../../create-account/createAccountContext';
import { scrollIntoViewAndFocus } from '../../common/functions/scrollIntoViewAndFocus';
import { TextField } from '@cmsgov/ds-medicare-gov';
import { HorizontalRule } from '../../common/components/HorizontalRule';

export type UpdatePasswordAndSecretQuestionErrorKeys = 'checkInfo' | 'inconvenience' | 'reEnter' | "invalidMBI" | "isAlreadyUsed" | "pleaseEnterUsername" | "passwordGuidelines";
export type UpdatePasswordAndSecretQuestionErrorKeysHeaders = 'completeItems' | 'cantFind' | 'cantProcess'
type UpdatePasswordAndSecretQuestionErrorListKeys = "enterPassword" | "createUsername" | "secretQuestion" | "secretAnswer" | "createPassword";
interface UpdatePasswordAndSecretQuestionProps {
  testInitialErrorState?: UpdatePasswordAndSecretQuestionErrorKeys & UpdatePasswordAndSecretQuestionErrorListKeys | null
  updatePasswordAndSecretHeaders?: UpdatePasswordAndSecretQuestionErrorKeysHeaders
}

const UpdatePasswordAndSecretQuestion = (props: UpdatePasswordAndSecretQuestionProps) => {
    const [confirmPasswordRef, setConfirmPasswordRef] = useState<HTMLElement | null>(null);
    const [passwordRef, setPasswordRef] = useState<HTMLElement | null>(null);
    const [secretAnswerRef, setSecretAnswerRef] = useState<HTMLElement | null>(null);
    const [secretQuestionRef, setSecretQuestionRef] = useState<HTMLElement | null>(null);
    const confirmPasswordError = useTrait(false);
    const passwordError = useTrait(false);
    const secretAnswerError = useTrait(false);
    const secretQuestionError = useTrait(false);
    const validateEntireForm = useTrait(false);
    const history = useHistory();
    const [isLoading, setIsLoading] = useState(false);
    const [isAlreadyUnlocked, setIsAlreadyUnlocked] = useState(false);
    const [alreadyUsed, setAlreadyUsed] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [password, setPassword] = useState('');
    const [hasPasswordError, setHasPasswordError] = useState(false);
    const [hasConfirmPasswordError, setHasConfirmPasswordError] = useState(false);

    const api = useUnlockAccountApi();
    const { data, updateData, stepBackward } = useContext(createOrUnlockAccountData);
    const { secretQuestion, secretAnswer, analyticsPart } = data;

    const conversion = useConversion('unlocked_account', 'unlocked_account');
    const [apiErrorMessage, setApiErrorMessage] = useState<JSX.Element | null | string>();
    const [errorKey, setErrorKey] = useState<UpdatePasswordAndSecretQuestionErrorKeys | null>(props.testInitialErrorState || null);
    const [errorListKey, setErrorListKey] = useState<UpdatePasswordAndSecretQuestionErrorListKeys | null>(props.testInitialErrorState || null);
    const [errorKeyHeader, setErrorKeyHeader] = useState<UpdatePasswordAndSecretQuestionErrorKeysHeaders | null>(props.updatePasswordAndSecretHeaders || null);
    const [errorAlertHeading, setErrorAlertHeading] = useState("");
    const [apiErrorMessageList, setApiErrorMessageList] = useState<JSX.Element[]>([]);
    const [analyticsErrorKey, setAnalyticsErrorKey] = useState<UpdatePasswordAndSecretQuestionErrorKeys | UpdatePasswordAndSecretQuestionErrorListKeys | null>(null);

    const currentListKey = useRef<string | null>(null);

    useEffect(() => {
        const errorHeaders: {
            [key in UpdatePasswordAndSecretQuestionErrorKeysHeaders]: string
        } = {
            completeItems: translate("complete_items_to_unlock_account"),
            cantFind: translate("we_cant_find_you"),
            cantProcess: translate("we_cant_process_request")
        }
        setErrorAlertHeading(errorKeyHeader ? errorHeaders[errorKeyHeader] : '')
    }, [setErrorAlertHeading, errorKeyHeader])

    useEffect(() => {
        const errorKeyToMessage: {
            [key in UpdatePasswordAndSecretQuestionErrorKeys]: string | JSX.Element
        } = {
            checkInfo: <>
                {translate("check_information_try_again")}<br />
                {translate("for_more_help_call")} (<a href="tel:18006334227">1-800-633-4227</a>).{" "}
                {translate("tty_users_can_call")}{" "}<a href="tel:18774862048">1-877-486-2048</a>.</>,
            isAlreadyUsed: <>
                <Button
                    variation={'ghost'}
                    analyticsData={{
                        text: translate('enter_a_new_password'),
                        linkUrl: translate('enter_a_new_password'),
                        parentComponentHeading: translate('unlock_account')
                    }}
                    onClick={() => passwordRef?.focus()}
                    style={{ padding: 0 }}
                >
                    {translate('enter_a_new_password')}
                </Button>
                {' '}
                {translate(`thats_different_from_last_six_passwords`)}
            </>,
            inconvenience: translate("we_are_sorry_for_inconvenience_try_unlocking_account_later"),
            reEnter: translate("please_reenter_information_and_tryagain"),
            invalidMBI: translate('please_check_mbi'),
            pleaseEnterUsername: translate('please_enter_username_meet_guidelines'),
            passwordGuidelines: translate('the_password_does_not_meet_required_enter_correct_info'),
        }
        setApiErrorMessage(errorKey ? errorKeyToMessage[errorKey] : null)
    }, [setApiErrorMessage, errorKey, passwordRef]);

    useEffect(() => {
        const errorKeyToMessage: {
            [key in UpdatePasswordAndSecretQuestionErrorListKeys]: JSX.Element
        } = {
            enterPassword: <>
                <ValidationAlertMessage
                    description={translate("enter_new_password_different_from_last_six")}
                    fieldReference={passwordRef}
                    linkText={translate("password")}
                    componentHeading={translate('unlock_account')}
                />
            </>,
            createPassword: <>
                <ValidationAlertMessage
                    description={translate("create_password_for_your_account")}
                    fieldReference={passwordRef}
                    linkText={translate("password")}
                    componentHeading={translate('unlock_account')}
                />
            </>,
            createUsername: <>
                <ValidationAlertMessage
                    description={translate("create_username_for_your_account")}
                    fieldReference={null}
                    linkText={translate("username")}
                    componentHeading={translate('unlock_account')}
                />
            </>,
            secretQuestion: <>
                <ValidationAlertMessage
                    description={translate("pick_secret_question")}
                    fieldReference={secretQuestionRef}
                    linkText={translate("secret_question")}
                    componentHeading={translate('unlock_account')}
                />
            </>,
            secretAnswer: <>
                <ValidationAlertMessage
                    description={translate("enter_answer_to_your_secret_question")}
                    fieldReference={secretAnswerRef}
                    linkText={translate("secret_answer")}
                    componentHeading={translate('unlock_account')}
                />
            </>
        }
        if (currentListKey.current === errorListKey) {
            return;
        }
        if (errorListKey && errorListKey in errorKeyToMessage) {
            setApiErrorMessageList([errorKeyToMessage[errorListKey], ...apiErrorMessageList]);
        } else {
            setApiErrorMessageList([]);
        }
        currentListKey.current = errorListKey;
    }, [errorListKey, apiErrorMessageList, secretAnswerRef, secretQuestionRef, passwordRef]);

    useEffect(() => {
        const errorStrings: {
            [key in UpdatePasswordAndSecretQuestionErrorKeys | UpdatePasswordAndSecretQuestionErrorListKeys]: string;
        } = {
            checkInfo: "Check the information you entered and try again. For more help, call 1-800-MEDICARE (1-800-633-4227). TTY users can call 1-877-486-2048.",
            inconvenience: "We're sorry for any inconvenience. Try unlocking your account later.",
            reEnter: "Please re-enter your information and try again.",
            invalidMBI: "Please check your Medicare Number.",
            isAlreadyUsed: "You entered a password you've used before.",
            pleaseEnterUsername: "Please enter a username that meets the username guidelines.",
            passwordGuidelines: "The Password does not meet the required guidelines. Please enter the correct information and try again.",
            enterPassword: "Enter a new password that's different from your last 6 passwords.",
            createPassword: "Create a password for your account using the guidelines listed.",
            createUsername: "Create a username for your account using the guidelines listed.",
            secretQuestion: "Pick a secret question.",
            secretAnswer: "Enter the answer to your secret question."
        };
        if (analyticsErrorKey) {
            conversion.failAPIIntermediate(errorStrings[analyticsErrorKey]);
            conversion.failAPI(errorStrings[analyticsErrorKey]);
        }
    }, [analyticsErrorKey, conversion]);

    const { setShowNewAccountLoginAlert } = useContext(globalData);

    const callUnlockAccount = async () => {
        const response = await api.step3(password, secretQuestion, secretAnswer);
        setIsLoading(false);
        const statusCode = response!.status;
        validateEntireForm.set(false);
        setAlreadyUsed(false);
        switch (statusCode) {
            case 204:
                conversion.success(analyticsPart);
                setShowNewAccountLoginAlert(true);
                history.push('/login');
                break;
            case 400:
                const invalidPasswordErrorKey: InvalidUsernameOrPasswordError | null = get400InvalidUsernameOrPasswordError(response?.data?.errors);
                const notVerified = response?.data?.errors?.some((error: ApiError) => error.message?.includes('not verified'));
                const didNotMatch = response?.data?.errors?.some((error: ApiError) => error.message?.includes('did not match'));
                const usernameCantBeMBI = response?.data?.errors?.some((error: ApiError) => error.message?.includes('username cannot be MBI'));
                const incorrectFormat = response?.data?.errors?.some((error: ApiError) => error.message?.includes('challengeQuestionAndAnswers'));

                if (invalidPasswordErrorKey === `isAlreadyUsed`){
                    passwordError.set(true);
                    confirmPasswordError.set(true);
                    setAlreadyUsed(true);
                    setErrorKeyHeader('completeItems');
                    setErrorListKey('enterPassword');
                    setAnalyticsErrorKey('enterPassword');
                } else if(invalidPasswordErrorKey) {
                    setErrorKeyHeader('completeItems');
                    setErrorListKey('createPassword');
                    setAnalyticsErrorKey('createPassword');
                } else if (notVerified || didNotMatch) {
                    setErrorKeyHeader('cantFind');
                    setErrorKey('checkInfo');
                    setAnalyticsErrorKey('checkInfo');
                } else if (usernameCantBeMBI) {
                    setErrorKeyHeader('completeItems');
                    setErrorListKey('createUsername');
                    setAnalyticsErrorKey('createUsername');
                } else if (incorrectFormat) {
                    setErrorKeyHeader('completeItems');
                    setErrorListKey('secretAnswer');
                    setErrorListKey('secretQuestion');
                    setAnalyticsErrorKey('secretAnswer');
                    setAnalyticsErrorKey('secretQuestion');
                } else {
                    setErrorKeyHeader('cantProcess');
                    setErrorKey('reEnter');
                    setAnalyticsErrorKey('reEnter');
                }
                break;
            case 404:
                const notLocked = response?.data?.errors?.some((error: ApiError) => error.message.includes('user is not locked'));
                if (notLocked) {
                    history.push('/login', {showAlreadyUnlockedAlert: true});
                } else {
                    setErrorKeyHeader('cantFind');
                    setErrorKey('checkInfo');
                    setAnalyticsErrorKey('checkInfo');
                }
                break;
            default:
                setErrorKeyHeader('cantProcess');
                setErrorKey('inconvenience');
                setAnalyticsErrorKey('inconvenience');
                break;
        }

        const errorMessage = getUnlockAccountError(statusCode, response?.data?.errors);
        setIsAlreadyUnlocked(isAlreadyUnlockedMessage(errorMessage));
        if (errorMessage && errorMessage === 'invalidMBI'){
            setErrorKey(errorMessage);
        }
    };

    const getApiErrorMessage = () => {
        const error = apiErrorMessage;
        if (!error) {
            return null;
        }
        return error;
    };

    const getValidationAlertMessages = () => {
        const messages: JSX.Element[] = [];
        if (!validateEntireForm.get())
            return messages;
        if (hasPasswordError || alreadyUsed)
            messages.push(
                <ValidationAlertMessage
                    description={alreadyUsed ? `${translate('enter_a_new_password')} ${translate("thats_different_from_last_six_passwords")}` : translate('create_password_for_your_account')}
                    fieldReference={passwordRef}
                    linkText={translate('password')}
                    componentHeading={translate('unlock_account')}
                />
            );
        if (hasConfirmPasswordError)
            messages.push(
                <ValidationAlertMessage
                    description={translate('reenter_new_password')}
                    fieldReference={passwordRef}
                    linkText={translate('confirm_password')}
                    componentHeading={translate('unlock_account')}
                />
            );
        if (secretQuestion === '')
            messages.push(
                <ValidationAlertMessage
                    description={translate('pick_secret_question')}
                    fieldReference={passwordRef}
                    linkText={translate('secret_question')}
                    componentHeading={translate('unlock_account')}
                />
            );
        if (secretAnswer === '')
            messages.push(
                <ValidationAlertMessage
                    description={translate('enter_secret_answer')}
                    fieldReference={passwordRef}
                    linkText={translate('secret_answer')}
                    componentHeading={translate('unlock_account')}
                />
            );
        return messages;
    };

    const handleUnlockAccountButtonClick = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setSubmitAttempted(true);
        resetErrors();

        if (isLoading) {
            return;
        }
        setIsLoading(true);

        conversion.attempt();
        const errorFieldRef = validateForm();
        if (errorFieldRef != null) {
            scrollIntoViewAndFocus(errorFieldRef);
            setIsLoading(false);
            return;
        }
        callUnlockAccount();
    };

    const resetErrors = () => {
        setErrorKey(null);
        setErrorKeyHeader(null);
        setErrorListKey(null);
        confirmPasswordError.set(false);
        passwordError.set(false);
        secretAnswerError.set(false);
        secretQuestionError.set(false);
    };

    const updatePassword = (value: string) => {
        setPassword(value);
    };

    const updateSecretAnswer = (value: string, doImmediateValidation = false) => {
        const newValue = value.trimStart();
        updateData({ secretAnswer: newValue });
        if (!doImmediateValidation && !validateEntireForm.get())
            return;
        validateSecretAnswer();
        if (validateEntireForm.get())
            validateForm();
    };

    const updateSecretQuestion = (value: string, doImmediateValidation = false) => {
        const newValue = value.trimStart();
        updateData({ secretQuestion: newValue });
        if (!doImmediateValidation && !validateEntireForm.get())
            return;
        validateSecretQuestion();
        if (validateEntireForm.get())
            validateForm();
    };

    const validateForm = (): HTMLElement | null | undefined => {
        if (!validateEntireForm.get())
            validateEntireForm.set(true);
        let errorFieldRef = null;
        if (hasPasswordError)
            errorFieldRef = passwordRef;
        if (hasConfirmPasswordError && !errorFieldRef)
            errorFieldRef = confirmPasswordRef;
        if (!validateSecretQuestion() && !errorFieldRef)
            errorFieldRef = secretQuestionRef;
        if (!validateSecretAnswer() && !errorFieldRef)
            errorFieldRef = secretAnswerRef;
        const messages = getValidationAlertMessages();

        if (messages.length) {
            const errorString = messages.map(x => x.props.description).join(', ');
            conversion.failValidation(errorString);
        }
        return messages.length ? errorFieldRef : null;
    };

    const validateSecretAnswer = () => {
        const isValid = secretAnswer !== '';
        if (secretAnswerError.get() === isValid)
            secretAnswerError.set(!isValid);
        return isValid;
    };

    const validateSecretQuestion = () => {
        const isValid = secretQuestion !== '';
        if (secretQuestionError.get() === isValid)
            secretQuestionError.set(!isValid);
        return isValid;
    };

    const getQuestionContext = () => {
        if (secretQuestionError.get() || errorListKey === 'secretQuestion') {
            return 'unlockAccount';
        }
        return null;
    }

    const getApiErrorKeyPassword = () => {
        if (errorListKey === 'createPassword' || errorListKey === 'enterPassword') {
            return errorListKey;
        }
    }

    const getSecretAnswerErrorMessage = () => {
        if (secretAnswerError.get()) {
            return translate('enter_secret_answer');
        } else if (errorListKey === 'secretAnswer') {
            return translate('check_answer_to_secret_question');
        } else {
            return null;
        }
    }

    return <>
        <form onSubmit={handleUnlockAccountButtonClick} method="POST">
            <fieldset style={{
                border: 0,
                margin: 0,
                padding: 0,
            }}>
                <Div isRow>
                    <Div cols={12}>
                        <legend>
                            <H2 className={'ds-u-margin-top--1 ds-u-margin-bottom--3'}>
                                {capitalizeFirstLetter(translate('update_password').toLowerCase())}{` `}&{` `}{translate('secret_question').toLowerCase()}
                            </H2>
                        </legend>
                        <AllFieldsRequired />
                        {(getApiErrorMessage() || apiErrorMessageList.length > 0) && getValidationAlertMessages().length === 0 &&
                            <ErrorAlert
                                heading={errorAlertHeading}
                                errorMessage={getApiErrorMessage()}
                                alreadyUnlocked={isAlreadyUnlocked}
                                validationMessages={apiErrorMessageList} />}
                        {getValidationAlertMessages().length !== 0 &&
                            <ErrorAlert
                                headingId={'validationAlertHeader'}
                                heading={translate('complete_items_to_unlock_account')}
                                validationMessages={getValidationAlertMessages()} />}
                        <H3>
                            {capitalizeFirstLetter(translate('update_password').toLowerCase())}
                        </H3>
                    </Div>
                </Div>
                <Div isRow>
                    <Div cols={12} colsMD={10}>
                        <NewPassword
                            component={'updatePasswordAndSecretQuestion'}
                            passwordLabel={translate('new_password')}
                            confirmPasswordLabel={translate('confirm_new_password')}
                            onPasswordChange={updatePassword}
                            submitAttempted={submitAttempted}
                            passwordRef={setPasswordRef}
                            confirmPasswordRef={setConfirmPasswordRef}
                            onPasswordError={setHasPasswordError}
                            onConfirmPasswordError={setHasConfirmPasswordError}
                            showAlreadyUsedError={alreadyUsed}
                            apiErrorKey={getApiErrorKeyPassword()}
                        />
                    </Div>
                </Div>
                <HorizontalRule marginBottom={2}/>
                <Div
                    isRow={true}
                    paddingTop={2}
                >
                    <Div cols={12}>
                        <H3>
                            {capitalizeFirstLetter(translate('update_secret_question').toLowerCase())}
                        </H3>
                    </Div>
                </Div>
                <Div isRow>
                    <Div cols={12}>
                        <SecretQuestionDropdown
                            inputRef={setSecretQuestionRef}
                            onBlur={event => updateSecretQuestion(event.currentTarget.value, is.to.be.validated)}
                            onChange={event => updateSecretQuestion(event.currentTarget.value)}
                            value={secretQuestion}
                            context={getQuestionContext()}
                        />
                        <TextField
                            errorMessage={getSecretAnswerErrorMessage()}
                            inputRef={setSecretAnswerRef}
                            label={translate('secret_answer')}
                            name={'secretAnswer'}
                            onBlur={event => updateSecretAnswer(event.currentTarget.value, is.to.be.validated)}
                            onChange={event => updateSecretAnswer(event.currentTarget.value)}
                            value={secretAnswer}
                        />
                    </Div>
                </Div>
            </fieldset>
            <StackingButtons
                button1={<Button
                    onClick={stepBackward}
                >
                    <TranslatedSpan english={'back'} />
                </Button>}
                button2={<Button
                    variation={'solid'}
                    mfaAnalyticsData={{
                        text: translate('unlock_account'),
                        parentComponentHeading: translate('unlock_account')
                    }}
                    type={'submit'}
                    isLoading={isLoading}
                >
                    {capitalizeWords(translate('unlock_account'))}
                </Button>}
            />
        </form>
    </>;
};

export default UpdatePasswordAndSecretQuestion;
